import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";

import {
  createRunnerShiftMutation,
  deleteRunnerShiftMutation,
  runnerShiftsQuery,
  updateRunnerShiftMutation,
} from "./queries";

const TOAST_MS = 3000;

function nullableTrim(value) {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function shiftFields(shift) {
  return {
    name: shift.name.trim(),
    description: nullableTrim(shift.description),
    startTime: shift.startTime,
    endTime: shift.endTime,
    gracePeriodMinutes: shift.gracePeriodMinutes,
    maxCollectPerSlot: shift.maxCollectPerSlot,
    maxParkPerSlot: shift.maxParkPerSlot,
    slotMinutes: shift.slotMinutes,
    isActive: shift.isActive,
  };
}

export default function useRunnerShifts(installationId) {
  const [toast, setToast] = useState(null);
  const [mutationError, setMutationError] = useState(null);
  const timer = useRef(null);

  const { data = {}, loading, error, refetch } = useQuery(runnerShiftsQuery, {
    variables: { installationId, includeInactive: true },
    skip: !installationId,
    fetchPolicy: "no-cache",
  });
  const [createShift, createState] = useMutation(createRunnerShiftMutation);
  const [updateShift, updateState] = useMutation(updateRunnerShiftMutation);
  const [deleteShift, deleteState] = useMutation(deleteRunnerShiftMutation);

  useEffect(() => () => clearTimeout(timer.current), []);

  const flash = (message) => {
    setToast(message);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(null), TOAST_MS);
  };

  const reloadShifts = () => refetch({ installationId, includeInactive: true });

  /* Every mutation returns false on failure so callers can keep their form open. */
  const run = async (mutate, message) => {
    setMutationError(null);

    try {
      await mutate();
      await reloadShifts();
      flash(message);
    } catch (err) {
      setMutationError(err);
      return false;
    }

    return true;
  };

  return {
    shifts: data.runnerShifts || [],
    loading,
    error,
    mutationError,
    mutationInFlight:
      createState.loading || updateState.loading || deleteState.loading,
    toast,
    create: (shift) =>
      run(
        () =>
          createShift({
            variables: { shift: { installationId, ...shiftFields(shift) } },
          }),
        `${shift.name.trim()} added.`,
      ),
    update: (shift) =>
      run(
        () =>
          updateShift({
            variables: { shift: { id: shift.id, ...shiftFields(shift) } },
          }),
        `${shift.name.trim()} updated.`,
      ),
    remove: (shift) =>
      run(
        () => deleteShift({ variables: { id: shift.id } }),
        `${shift.name} removed.`,
      ),
  };
}
